import { useState } from 'react';
import { Radio } from 'antd';

import { Button, Dialog, Field, Input, Select } from '../../../components/ui';
import { useLoi } from '../../../hooks/useLoi';
import { useT } from '../../../i18n';
import type { KhoaDich } from '../../../i18n';
import { soVN } from '../../../lib/dinh-dang';
import { loiTheoTruong } from '../../../lib/errors';
import type { ApiError } from '../../../lib/errors';
import { ChonPhongBan } from '../../co-cau-to-chuc/components/ChonPhongBan';
import { ChonNguoiDung } from '../../nhat-ky/components/ChonNguoiDung';
import { docSoToken } from '../../tenant/nghiep-vu/han-muc-token';
import { LoiTrongHop } from '../../tenant/components/phan-form';
import { CAC_CHU_KY, CHU_KY, HANH_DONG, PHAM_VI } from '../constants';
import { useSuaHanMucAi, useTaoHanMucAi } from '../hooks/useTokenAi';
import {
  FORM_TRONG,
  formTuHanMuc,
  kiemFormHanMucAi,
  thanSuaHanMuc,
  thanTaoHanMuc,
  type FormHanMucAi,
  type LoiFormHanMucAi,
  type LoiGioiHan,
} from '../nghiep-vu/token-ai';
import type { AiLimit, DongPhanRa, HanhDongHanMucAi, PhamViHanMucAi } from '../types';

const LOI_GIOI_HAN: Record<LoiGioiHan, KhoaDich> = {
  trong: 'manTokenAi.loiSoTrong',
  sai: 'manTokenAi.loiSoSai',
  'qua-lon': 'manTokenAi.loiSoQuaLon',
  khong: 'manTokenAi.loiSoKhong',
};
const CAC_PHAM_VI: PhamViHanMucAi[] = ['DON_VI', 'NGUOI_DUNG'];
const CAC_HANH_DONG: HanhDongHanMucAi[] = ['CHAN', 'CANH_BAO'];

/**
 * Hộp thêm, sửa một hạn mức tự đặt. Sửa không đổi được phạm vi và đối tượng (một dòng là một quy tắc của đúng đối tượng
 * đó). `409` trùng tổ hợp hiện dưới ô đối tượng, câu của backend giữ nguyên; lỗi theo trường hiện dưới đúng ô; lỗi khác
 * vào dải trong hộp. Hộp không đóng khi lỗi. Nơi dùng dựng lại hộp mỗi lần mở nên không cần đồng bộ state.
 */
export function HopHanMucAi({
  tenantId,
  dang,
  tinhNangCoSan,
  onClose,
}: {
  tenantId: string;
  dang: AiLimit | null;
  tinhNangCoSan: readonly DongPhanRa[];
  onClose: () => void;
}) {
  const { t, locale } = useT();
  const dichLoi = useLoi();
  const [f, setF] = useState<FormHanMucAi>(() => (dang ? formTuHanMuc(dang) : FORM_TRONG));
  const [soDaNhap, setSoDaNhap] = useState(() => (dang ? soVN(dang.gioi_han, locale) : ''));
  const [loiKiem, setLoiKiem] = useState<LoiFormHanMucAi>({});
  const tao = useTaoHanMucAi(tenantId);
  const sua = useSuaHanMucAi(tenantId);
  const dangGui = tao.isPending || sua.isPending;
  const loiGui = tao.error ?? sua.error;
  const truong = loiTheoTruong(loiGui);
  const trung = (loiGui as ApiError | null)?.status === 409;
  const loiChung = loiGui && !trung && Object.keys(truong).length === 0 ? dichLoi(loiGui) : null;

  const doi = <K extends keyof FormHanMucAi>(k: K, v: FormHanMucAi[K]) => {
    setF((cu) => ({ ...cu, [k]: v }));
    setLoiKiem((cu) => ({ ...cu, ...(k === 'khoa' ? { khoa: undefined } : {}) }));
    tao.reset();
    sua.reset();
  };

  const luu = () => {
    const form = { ...f, gioiHan: soDaNhap };
    const loi = kiemFormHanMucAi(form);
    setLoiKiem(loi);
    if (loi.khoa || loi.gioiHan) return;
    if (dang) {
      const du = thanSuaHanMuc(dang, form);
      if (Object.keys(du).length === 0) {
        onClose();
        return;
      }
      sua.mutate({ id: dang.id, du }, { onSuccess: onClose });
    } else {
      tao.mutate(thanTaoHanMuc(form), { onSuccess: onClose });
    }
  };

  const loiDoiTuong = trung
    ? dichLoi(loiGui)
    : (truong['/khoa'] ?? (loiKiem.khoa ? t(f.phamVi === 'DON_VI' ? 'manTokenAi.loiChonPhongBan' : 'manTokenAi.loiChonNguoi') : undefined));

  // Tính năng đang đặt có thể chưa có số liệu tháng này: vẫn phải hiện trong ô.
  const luaChonTinhNang = [
    ...tinhNangCoSan.flatMap((d) => (d.khoa ? [{ value: d.khoa, label: d.ten ?? d.khoa }] : [])),
    ...(f.tinhNang && !tinhNangCoSan.some((d) => d.khoa === f.tinhNang) ? [{ value: f.tinhNang, label: f.tinhNang }] : []),
  ];

  return (
    <Dialog
      open
      onClose={onClose}
      title={t(dang ? 'manTokenAi.suaHanMuc' : 'manTokenAi.themHanMuc')}
      width={520}
      destroyOnHidden
      footer={
        <div className="flex flex-wrap justify-end gap-3">
          <Button variant="outline" size="md" onClick={onClose}>
            {t('chung.huy')}
          </Button>
          <Button variant="primary" size="md" loading={dangGui} onClick={luu}>
            {dangGui ? t('manTokenAi.dangLuu') : t(dang ? 'manTokenAi.luuThayDoi' : 'manTokenAi.themHanMuc')}
          </Button>
        </div>
      }
    >
      <div className="space-y-4 pt-2">
        {dang ? (
          <Field label={t(dang.pham_vi === 'DON_VI' ? 'manTokenAi.phamViDonVi' : 'manTokenAi.phamViNguoi')} error={loiDoiTuong}>
            {() => <p className="text-sm font-medium text-content-primary">{dang.ten_khoa ?? t('manTokenAi.doiTuongKhongCon')}</p>}
          </Field>
        ) : (
          <>
            <Field label={t('manTokenAi.cotPhamVi')} required>
              {(p) => (
                <Radio.Group
                  id={p.id}
                  value={f.phamVi}
                  onChange={(e) => {
                    doi('phamVi', e.target.value as PhamViHanMucAi);
                    doi('khoa', null);
                  }}
                >
                  {CAC_PHAM_VI.map((v) => (
                    <Radio key={v} value={v}>
                      {t(PHAM_VI[v].nhan)}
                    </Radio>
                  ))}
                </Radio.Group>
              )}
            </Field>
            {f.phamVi === 'DON_VI' ? (
              <ChonPhongBan
                required
                value={f.khoa}
                onChange={(id) => doi('khoa', id)}
                placeholder={t('manTokenAi.chonPhongBan')}
                error={loiDoiTuong}
              />
            ) : (
              <Field label={t('manTokenAi.phamViNguoi')} required error={loiDoiTuong}>
                {(p) => (
                  <ChonNguoiDung
                    tenantId={tenantId}
                    nenTang={false}
                    className="w-full"
                    fieldProps={p}
                    value={f.khoa ?? undefined}
                    onChange={(id) => doi('khoa', id ?? null)}
                  />
                )}
              </Field>
            )}
          </>
        )}

        <Field label={t('manTokenAi.cotTinhNang')} error={truong['/tinh_nang']}>
          {(p) => (
            <Select<string>
              {...p}
              className="w-full"
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder={t('manTokenAi.moiTinhNang')}
              value={f.tinhNang ?? undefined}
              onChange={(v) => doi('tinhNang', v ?? null)}
              options={luaChonTinhNang}
            />
          )}
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t('manTokenAi.cotChuKy')} required error={truong['/chu_ky']}>
            {(p) => (
              <Select
                {...p}
                className="w-full"
                value={f.chuKy}
                onChange={(v) => doi('chuKy', v)}
                options={CAC_CHU_KY.map((v) => ({ value: v, label: t(CHU_KY[v]) }))}
              />
            )}
          </Field>
          <Field
            label={t('manTokenAi.soToken')}
            required
            error={truong['/gioi_han'] ?? (loiKiem.gioiHan ? t(LOI_GIOI_HAN[loiKiem.gioiHan]) : undefined)}
          >
            {(p) => (
              <Input
                {...p}
                inputMode="numeric"
                value={soDaNhap}
                placeholder={soVN(20000, locale)}
                onChange={(e) => {
                  setSoDaNhap(e.target.value);
                  setLoiKiem((cu) => ({ ...cu, gioiHan: undefined }));
                }}
                onBlur={() => {
                  const so = docSoToken(soDaNhap);
                  if (so.ok) setSoDaNhap(soVN(so.so, locale));
                }}
              />
            )}
          </Field>
        </div>

        <Field
          label={t('manTokenAi.cotHanhDong')}
          required
          hint={t(f.hanhDong === 'CHAN' ? 'manTokenAi.goiYChan' : 'manTokenAi.goiYCanhBao')}
          error={truong['/hanh_dong']}
        >
          {(p) => (
            <Radio.Group id={p.id} value={f.hanhDong} onChange={(e) => doi('hanhDong', e.target.value as HanhDongHanMucAi)}>
              {CAC_HANH_DONG.map((v) => (
                <Radio key={v} value={v}>
                  {t(HANH_DONG[v].nhan)}
                </Radio>
              ))}
            </Radio.Group>
          )}
        </Field>

        {loiChung && <LoiTrongHop>{loiChung}</LoiTrongHop>}
      </div>
    </Dialog>
  );
}
